import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Table } from 'ksnm-common-ui/lib/Table2/Table';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';

import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { constantStore } from '@stores/constantStore';
import { dataControlStore } from '@stores/dataControlStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { getColumns } from './columns';
import { useGetPremiseData } from './hooks/useGetPremiseData';
import { useUpdateTitle } from './hooks/useUpdateTitle';
import { Title } from './Title';

import styles from '../DataControl.module.scss';

const { TABLE_NAMES } = constantStore;

export const ControlLevel = observer(() => {
  const [isPremiseLoadingDone, setIsPremiseLoadingDone] = useState(false);

  const { lastPagesParam, filters, setNeedUpdateFilters } = dataControlStore;
  const { maxExecNum } = lastPagesParam;
  const { controlType, status, runNumber } = filters.controlLevel || {};

  const { titleData, updateTitle } = useUpdateTitle();
  const [title, onChangeTable] = useTableTitleWithCount(<Title data={titleData} />);

  const [key, update] = useUpdateTable({ tableName: TABLE_NAMES.CONTROL_LEVEL });

  const premiseData = useGetPremiseData(lastPagesParam, setIsPremiseLoadingDone);

  const hardFilter = useMemo(() => {
    const out = [...(lastPagesParam.filters?.[0] ?? [])];

    if (runNumber) {
      out.push({
        name: 'exec_num',
        type: FILTER.EQ,
        values: [runNumber],
        filterIsInverted: false,
      });
    }

    if (controlType) {
      out.push({
        name: 'step_type',
        type: FILTER.EQ,
        values: [controlType],
        filterIsInverted: false,
      });
    }

    if (status) {
      out.push({
        name: 'status',
        type: FILTER.EQ,
        values: [status],
        filterIsInverted: false,
      });
    }

    update();

    return [out];
  }, [controlType, status, lastPagesParam.filters, runNumber, update]);

  const columns = useMemo(() => getColumns(premiseData), [premiseData]);

  const onRefresh = useCallback(() => {
    update();
    updateTitle();
    setNeedUpdateFilters(true);
  }, [setNeedUpdateFilters, update, updateTitle]);

  const extendTableButtons = useMemo(
    () => (
      <>
        <IconButton key="refresh" iconName="refresh" title="Обновить" onClick={onRefresh} />
      </>
    ),
    [onRefresh],
  );

  const rowClassName = useCallback(() => {
    if (!maxExecNum) return '';
    return maxExecNum === filters.controlLevel?.runNumber ? '' : styles.bgRowYellow;
  }, [filters.controlLevel?.runNumber, maxExecNum]);

  if (!isPremiseLoadingDone || !runNumber) return null;

  return (
    <Table
      columns={columns}
      tableTitle={title}
      tableName={TABLE_NAMES.CONTROL_LEVEL}
      resource="v_ctrl_ctrl"
      externalFetch={ksnmTableStore.externalFetch}
      externalFetchXLSX={ksnmTableStore.getExcelStreamV2}
      hardFilter={hardFilter}
      onTableChange={onChangeTable}
      showAggregateRow={false}
      cellHeight={64}
      key={key}
      extendTableButtons={extendTableButtons}
      rowClassName={rowClassName}
    />
  );
});
